import { useCallback, useEffect, useMemo, useState } from 'react'
import { type Location, unstable_useBlocker as useBlocker } from 'react-router-dom'

import { Dialog } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

export const useNavConfirmModal = () => {
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null)
  const [navigationBlocked, setNavigationBlocked] = useState(false)

  // Browser refresh/close tab blocking
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (navigationBlocked) {
        event.preventDefault()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [navigationBlocked])

  // Handle browser back/forward button
  useEffect(() => {
    const handlePopState = () => {
      if (navigationBlocked) {
        // Push state again immediately to stay on current page
        window.history.pushState(null, '', window.location.href)

        // Show the Dialog modal
        setConfirmModalOpen(true)
        // Set pending navigation to go back when user confirms
        setPendingNavigation(() => () => {
          window.history.back()
        })
      }
    }

    // Push initial state to enable popstate detection
    if (navigationBlocked) {
      window.history.pushState(null, '', window.location.href)
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [navigationBlocked])

  // React Router navigation blocking
  const shouldBlock = useCallback(
    ({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) => {
      return navigationBlocked && currentLocation.pathname !== nextLocation.pathname
    },
    [navigationBlocked]
  )
  const blocker = useBlocker(shouldBlock)

  // Show modal when navigation is blocked
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setConfirmModalOpen(true)
      setPendingNavigation(() => () => blocker.proceed())
    }
  }, [blocker])

  // Continue editing
  const onContinue = useCallback(async () => {
    setConfirmModalOpen(false)
    setPendingNavigation(null)
  }, [])

  // Discard changes and proceed with navigation
  const onDiscard = useCallback(() => {
    setNavigationBlocked(false)
    setConfirmModalOpen(false)
    pendingNavigation?.()
    setPendingNavigation(null)
  }, [pendingNavigation])

  // Memoize the component to prevent unnecessary re-creation
  const NavConfirmModalWrapper = useMemo(() => {
    const Component = () => (
      <NavConfirmModal
        open={confirmModalOpen}
        onClose={onContinue}
        onDiscard={onDiscard}
        title={i18n.t('nav_confirm_modal.title')}
        description={i18n.t('nav_confirm_modal.description')}
        primaryButtonLabel={i18n.t('nav_confirm_modal.continue_editing')}
        secondaryButtonLabel={i18n.t('nav_confirm_modal.discard')}
      />
    )
    Component.displayName = 'NavConfirmModalWrapper'
    return Component
  }, [confirmModalOpen, onContinue, onDiscard])

  return {
    NavConfirmModal: NavConfirmModalWrapper,
    setNavigationBlocked,
  }
}

export type NavConfirmModalProps = {
  open: boolean
  onClose?: () => void
  onDiscard?: () => void
  title: string
  description: string
  primaryButtonLabel: string
  secondaryButtonLabel: string
}

const NavConfirmModal = ({
  open,
  onClose,
  onDiscard,
  title,
  description,
  primaryButtonLabel,
  secondaryButtonLabel,
}: NavConfirmModalProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      header={{
        type: 'critical',
        title: title,
        description: description,
      }}
      actions={{
        primary: {
          label: primaryButtonLabel,
          onClick: onClose,
        },
        secondary: {
          label: secondaryButtonLabel,
          onClick: onDiscard,
        },
      }}
    />
  )
}
